/*
  Brand Guidelines: self-building sticky menu.
  Custom file (not part of Turbo). See CUSTOMIZATIONS.md and BRAND_GUIDELINES_PLAN.md.

  <brand-guide-nav> is a custom element, so it sets itself up whenever its markup is inserted and cleans up
  when removed. That covers first page load, Turbo's InstantClick page swaps and the theme editor re-rendering
  the menu section. Chapter sections changing in the editor are handled through the shopify:section:* events.

  Turbo's header is not CSS-sticky. It is a fixed bar that only appears after scrolling:
    - desktop: a clone of .main-nav__wrapper (.sticky_nav) that gets .sticky_nav--stick, when "Fixed header" is on
      (Turbo then keeps .header-fixed--true on the header)
    - mobile: #header, fixed while .header-group has .sticky, when .mobile_nav-fixed--true is present
  So the menu's `top` follows whether that bar is showing right now (--brand-guide-header), while anchor jumps
  always leave room for it (--brand-guide-offset), because the bar is showing by the time a jump lands.
*/
(function () {
  if (!window.customElements || window.customElements.get('brand-guide-nav')) return;

  var desktop = window.matchMedia('(min-width: 799px)');
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var GAP = 16; // space between the menu and a chapter's top edge after a jump
  var root = document.documentElement;

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  // Height of Turbo's desktop clone, cached the first time it is showing. Until then the regular header bar
  // stands in (a few px shorter). The clone is never measured while hidden: forcing it visible breaks
  // Turbo's slide-in animation.
  var desktopHeaderHeight = 0;

  // { enabled, stuck, height } of Turbo's fixed header for the current breakpoint
  function getHeaderState() {
    if (desktop.matches) {
      if (!document.querySelector('.header-fixed--true')) return { enabled: false, stuck: false, height: 0 };
      var clone = document.querySelector('.main-nav__wrapper.sticky_nav');
      var stuck = !!(clone && clone.classList.contains('sticky_nav--stick'));
      if (stuck && clone.offsetHeight) desktopHeaderHeight = clone.offsetHeight;
      var original = document.querySelector('.main-nav__wrapper:not(.sticky_nav)');
      return {
        enabled: true,
        stuck: stuck,
        height: desktopHeaderHeight || (original ? original.offsetHeight : 0)
      };
    }

    if (!document.querySelector('.mobile_nav-fixed--true')) return { enabled: false, stuck: false, height: 0 };
    var group = document.querySelector('.header-group');
    var topBar = document.querySelector('#header .top-bar');
    var promo = document.querySelector('#header .promo-banner');
    // Same formula Turbo uses (promo + top bar), so an open mobile menu doesn't count
    var height = (topBar ? topBar.offsetHeight : 0) + (promo ? promo.offsetHeight : 0);
    return { enabled: true, stuck: !!(group && group.classList.contains('sticky')), height: height };
  }

  class BrandGuideNav extends HTMLElement {
    connectedCallback() {
      this.nav = this.querySelector('[data-brand-nav]');
      this.list = this.querySelector('[data-brand-nav-list]');
      if (!this.nav || !this.list) return;

      this.sticky = this.nav.classList.contains('brand-guide-nav--sticky');
      this.links = new Map();
      this.chapters = [];
      this.intersecting = new Set();
      this.activeId = null;
      this.headerStuck = null;
      this.line = 0;
      this.frame = null;
      this.scrollTimeout = null;
      this.rebuildTimeout = null;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onIntersect = this.onIntersect.bind(this);
      this.scheduleRebuild = this.scheduleRebuild.bind(this);

      this.list.addEventListener('click', this.onClick);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      desktop.addEventListener('change', this.onResize);

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.nav);
        // Turbo's .sticky_nav clone is picked up on scroll instead (see check())
        ['.main-nav__wrapper:not(.sticky_nav)', '#header', '.header-group'].forEach(function (selector) {
          var el = document.querySelector(selector);
          if (el) this.resizeObserver.observe(el);
        }, this);
      }

      if (isDesignMode()) {
        ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder', 'shopify:block:select'].forEach(
          function (name) {
            document.addEventListener(name, this.scheduleRebuild);
          },
          this
        );
      }

      this.build();
      this.alignToHash();
    }

    disconnectedCallback() {
      if (!this.list) return;
      this.list.removeEventListener('click', this.onClick);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      desktop.removeEventListener('change', this.onResize);
      ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder', 'shopify:block:select'].forEach(
        function (name) {
          document.removeEventListener(name, this.scheduleRebuild);
        },
        this
      );
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.observer) this.observer.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.scrollTimeout);
      clearTimeout(this.rebuildTimeout);
      root.style.removeProperty('--brand-guide-header');
      root.style.removeProperty('--brand-guide-offset');
    }

    /* ------------------------------------------------------------ Menu */

    build() {
      var chapters = Array.prototype.slice.call(document.querySelectorAll('[data-brand-chapter][data-nav-label]'));
      var seen = {};
      this.list.textContent = '';
      this.links.clear();
      this.chapters = [];

      chapters.forEach(function (chapter) {
        if (!chapter.id) return;
        if (seen[chapter.id]) {
          if (isDesignMode()) {
            console.warn(
              'Brand guidelines menu: two chapters use the menu link ID "#' + chapter.id + '". Only the first is linked.'
            );
          }
          return;
        }
        seen[chapter.id] = true;

        var item = document.createElement('li');
        item.className = 'brand-guide-nav__item';
        var link = document.createElement('a');
        link.className = 'brand-guide-nav__link';
        link.href = '#' + chapter.id;
        link.textContent = chapter.getAttribute('data-nav-label');
        link.setAttribute('data-no-instant', ''); // keep InstantClick away from in-page links
        item.appendChild(link);
        this.list.appendChild(item);

        this.links.set(chapter.id, link);
        this.chapters.push(chapter);
      }, this);

      this.nav.hidden = this.chapters.length === 0;
      this.activeId = null;
      this.headerStuck = null;
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null; // new chapter list: observe() must start over
      }
      this.measure();
    }

    scheduleRebuild() {
      // Unload fires before the section is removed, so wait for the DOM to settle
      clearTimeout(this.rebuildTimeout);
      this.rebuildTimeout = setTimeout(this.build.bind(this), 50);
    }

    /* ------------------------------------------------------------ Offsets */

    measure() {
      var header = getHeaderState();
      var navHeight = this.sticky && !this.nav.hidden ? this.nav.offsetHeight : 0;
      var headerNow = header.enabled && header.stuck ? header.height : 0;

      root.style.setProperty('--brand-guide-header', (this.sticky ? headerNow : 0) + 'px');
      root.style.setProperty('--brand-guide-offset', (header.enabled ? header.height : 0) + navHeight + GAP + 'px');

      this.headerStuck = header.stuck;
      this.headerHeight = header.height;
      this.observe(headerNow + navHeight + GAP);
    }

    onResize() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(this.measure.bind(this));
    }

    onScroll() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(this.check.bind(this));
      // Turbo toggles its header on a throttle, so check again once scrolling settles
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(this.check.bind(this), 350);
    }

    check() {
      // Also re-measure when the height changes: Turbo's header can still be animating when it first sticks
      var header = getHeaderState();
      if (header.stuck !== this.headerStuck || header.height !== this.headerHeight) this.measure();
      this.checkPageEdges();
    }

    /* ------------------------------------------------------------ Active link */

    // Watches a 1px line just under the menu: the chapter crossing it is the current one
    observe(line) {
      if (!window.IntersectionObserver || !this.chapters.length) return;
      // The bottom margin depends on the viewport height, so a resize needs a new observer too
      var key = line + ':' + window.innerHeight;
      if (this.observer && key === this.observerKey) return;
      this.observerKey = key;
      this.line = line;
      if (this.observer) this.observer.disconnect();
      this.intersecting.clear();

      var bottom = Math.max(window.innerHeight - line - 1, 0);
      this.observer = new IntersectionObserver(this.onIntersect, {
        rootMargin: -line + 'px 0px ' + -bottom + 'px 0px',
        threshold: 0
      });
      this.chapters.forEach(function (chapter) {
        this.observer.observe(chapter);
      }, this);
    }

    onIntersect(entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          this.intersecting.add(entry.target.id);
        } else {
          this.intersecting.delete(entry.target.id);
        }
      }, this);

      var current = null;
      this.chapters.forEach(function (chapter) {
        if (this.intersecting.has(chapter.id)) current = chapter.id;
      }, this);
      if (current) this.setActive(current);
      this.checkPageEdges();
    }

    // Above the first chapter nothing is current; at the very bottom the last chapter is,
    // even when it is too short to reach the line
    checkPageEdges() {
      if (!this.chapters.length) return;
      var atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      if (atBottom) {
        this.setActive(this.chapters[this.chapters.length - 1].id);
      } else if (this.chapters[0].getBoundingClientRect().top > this.line + 2) {
        this.setActive(null);
      }
    }

    setActive(id) {
      if (id === this.activeId) return;
      if (this.activeId && this.links.has(this.activeId)) {
        var previous = this.links.get(this.activeId);
        previous.classList.remove('is-active');
        previous.removeAttribute('aria-current');
      }
      this.activeId = id;
      if (!id || !this.links.has(id)) return;

      var link = this.links.get(id);
      link.classList.add('is-active');
      link.setAttribute('aria-current', 'true');
      this.revealInList(link.parentElement);
    }

    // Mobile: scroll the menu sideways only. scrollIntoView would also move the page vertically.
    revealInList(item) {
      var list = this.list;
      if (list.scrollWidth <= list.clientWidth) return;
      var left = item.offsetLeft - (list.clientWidth - item.offsetWidth) / 2;
      list.scrollTo({ left: Math.max(left, 0), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    /* ------------------------------------------------------------ Clicks and deep links */

    onClick(event) {
      var link = event.target.closest('a[href^="#"]');
      if (!link) return;
      var target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
      if (!target) return;

      event.preventDefault();
      // Smooth (or not, for reduced motion) via CSS scroll-behavior
      window.scrollTo({ top: this.destination(target) });
      // Replace rather than push, so the back button leaves the page instead of stepping through chapters
      history.replaceState(history.state, '', link.hash);

      // Move keyboard focus to the chapter without a second scroll
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }

    // Scroll position that puts a chapter just under the menu. Leaves room for Turbo's header only if the header
    // will be showing there: on desktop it only appears past a fixed scroll depth (Headhesive's scrollOffset,
    // 700px), so chapters near the top would otherwise land with an empty gap above them.
    destination(target) {
      var header = getHeaderState();
      var navHeight = this.sticky && !this.nav.hidden ? this.nav.offsetHeight : 0;
      var top = target.getBoundingClientRect().top + window.scrollY;
      var withHeader = top - (header.enabled ? header.height : 0) - navHeight - GAP;
      var threshold = desktop.matches && window.sticky_nav ? window.sticky_nav.scrollOffset : null;
      if (header.enabled && typeof threshold === 'number' && withHeader <= threshold) {
        return Math.max(top - navHeight - GAP, 0);
      }
      return Math.max(withHeader, 0);
    }

    // On load with #chapter in the URL, the browser jumps before the offsets exist, and the page keeps shifting
    // while Turbo's header, fonts and images load. Realign now and again once everything has loaded.
    alignToHash() {
      if (!location.hash || isDesignMode()) return;
      var target = document.getElementById(decodeURIComponent(location.hash.slice(1)));
      if (!target || !target.hasAttribute('data-brand-chapter')) return;

      var self = this;
      var align = function () {
        self.measure();
        window.scrollTo({ top: self.destination(target), behavior: 'instant' });
      };
      requestAnimationFrame(align);
      if (document.readyState !== 'complete') {
        window.addEventListener(
          'load',
          function () {
            setTimeout(align, 100);
          },
          { once: true }
        );
      }
    }
  }

  window.customElements.define('brand-guide-nav', BrandGuideNav);
})();
